import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ShoppingBag, Shield, Zap, Send, Package
} from "lucide-react";

/* ── Language switcher ── */
const LANGS = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "ar", label: "ع" },
];

const FEATURE_ICONS = [Shield, Zap, Send, Package];
const STEP_GLYPHS   = ["◈", "⬡", "◇", "✦", "⌘"];

export default function HowItWorksPage() {
  const { t, i18n } = useTranslation();
  const [activeLang, setActiveLang] = useState(i18n.language?.slice(0, 2) || "en");

  const switchLang = (code) => {
    setActiveLang(code);
    i18n.changeLanguage(code);
  };

  const rawSteps    = t("howItWorks.steps",           { returnObjects: true });
  const rawFeatures = t("howItWorks.features.items",  { returnObjects: true });
  const steps    = Array.isArray(rawSteps)    ? rawSteps    : [];
  const features = Array.isArray(rawFeatures) ? rawFeatures : [];

  const isRtl = activeLang === "ar";

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      style={{
        minHeight: "100vh",
        background: "#060d0a",
        overflowX: "hidden",
        fontFamily: "'Cormorant Garamond','Georgia',serif",
        position: "relative",
      }}
    >
      <style>{CSS}</style>

      <div className="hiw-grid-bg" aria-hidden="true" />
      <div className="hiw-glow hiw-glow-1" aria-hidden="true" />
      <div className="hiw-glow hiw-glow-2" aria-hidden="true" />
      <div className="hiw-orb hiw-orb-1" aria-hidden="true" />
      <div className="hiw-orb hiw-orb-2" aria-hidden="true" />

      <div className="hiw-container">

        {/* ── Language bar ── */}
        <div className={`hiw-lang-row${isRtl ? " rtl" : ""}`}>
          <div className="hiw-lang-bar">
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => switchLang(l.code)}
                className={`hiw-lang-btn${activeLang === l.code ? " active" : ""}`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* ══ HERO ══ */}
        <motion.div
          className="hiw-hero"
          initial={{ opacity: 0, y: 36 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Logo image – replaces hardcoded "Smart Shopping" text */}
          <div className="hiw-logo-badge">
            <img
              src="/logo.png"
              alt="Dar Oulmes"
              className="hiw-logo-img"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.nextSibling.style.display = "block";
              }}
            />
            <span className="hiw-logo-fallback">Dar Oulmes</span>
          </div>

          <h1 className="hiw-hero-title">{t("howItWorks.hero.title")}</h1>
          <p className="hiw-hero-sub">{t("howItWorks.hero.subtitle")}</p>

          <div className="hiw-divider">
            <span className="hiw-divider-dot" />
            <span className="hiw-divider-bar" />
            <span className="hiw-divider-dot" />
          </div>
        </motion.div>

        {/* ══ TIMELINE ══ */}
        <div className="hiw-timeline-wrap">

          {/* Spine – zero-width wrapper keeps it exactly centred */}
          <div className="hiw-spine" aria-hidden="true">
            <div className="hiw-spine-line" />
          </div>

          {steps.map((step, idx) => {
            const isEven = idx % 2 === 0; /* even → card LEFT, odd → card RIGHT */
            return (
              <motion.div
                key={idx}
                className="hiw-step-row"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-70px" }}
                transition={{ delay: idx * 0.1, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* LEFT slot – holds card only on even steps */}
                <div className="hiw-slot hiw-slot-left">
                  {isEven && (
                    <div className="hiw-card">
                      <div className="hiw-card-top">
                        <span className="hiw-card-glyph">{STEP_GLYPHS[idx % STEP_GLYPHS.length]}</span>
                        <span className="hiw-card-label">STEP {String(idx + 1).padStart(2, "0")}</span>
                      </div>
                      <h3 className="hiw-card-title">{step.title}</h3>
                      <p  className="hiw-card-desc">{step.description}</p>
                    </div>
                  )}
                </div>

                {/* CENTRE slot – always has the number bubble */}
                <div className="hiw-slot hiw-slot-center">
                  <div className="hiw-bubble">
                    <span className="hiw-bubble-num">{idx + 1}</span>
                    <div className="hiw-bubble-ring" aria-hidden="true" />
                    <div className="hiw-bubble-ring hiw-bubble-ring-2" aria-hidden="true" />
                  </div>
                </div>

                {/* RIGHT slot – holds card only on odd steps */}
                <div className="hiw-slot hiw-slot-right">
                  {!isEven && (
                    <div className="hiw-card">
                      <div className="hiw-card-top">
                        <span className="hiw-card-glyph">{STEP_GLYPHS[idx % STEP_GLYPHS.length]}</span>
                        <span className="hiw-card-label">STEP {String(idx + 1).padStart(2, "0")}</span>
                      </div>
                      <h3 className="hiw-card-title">{step.title}</h3>
                      <p  className="hiw-card-desc">{step.description}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* ══ FEATURES GRID ══ */}
        <motion.div
          className="hiw-features-wrap"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
        >
          <p className="hiw-section-eyebrow">{t("howItWorks.features.title")}</p>

          <div className="hiw-feat-grid">
            {features.map((feat, idx) => {
              const Icon = FEATURE_ICONS[idx % FEATURE_ICONS.length];
              return (
                <motion.div
                  key={idx}
                  className="hiw-feat-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="hiw-feat-icon">
                    <Icon size={20} color="#c8a065" />
                  </div>
                  <h3 className="hiw-feat-title">{feat.title}</h3>
                  <p  className="hiw-feat-desc">{feat.description}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* ══ CTA BANNER ══ */}
        <motion.div
          className="hiw-cta"
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="hiw-cta-shimmer" aria-hidden="true" />
          <div className="hiw-cta-glow"    aria-hidden="true" />
          <div className="hiw-cta-inner">
            <p className="hiw-cta-eyebrow">Dar Oulmes</p>
            <h3 className="hiw-cta-title">{t("howItWorks.cta.title")}</h3>
            <Link to="/products" className="hiw-cta-btn">
              {t("howItWorks.cta.button")}
              <ShoppingBag size={16} />
            </Link>
          </div>
        </motion.div>

      </div>
    </div>
  );
}

/* ════════════════════════════════════════
   CSS
════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300&family=Montserrat:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }

  /* ── Grid bg ── */
  .hiw-grid-bg {
    position:fixed; inset:0; pointer-events:none; z-index:0;
    background-image:
      linear-gradient(rgba(200,160,101,.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(200,160,101,.04) 1px, transparent 1px);
    background-size:52px 52px;
    mask-image:radial-gradient(ellipse 85% 85% at 50% 50%, black 30%, transparent 100%);
  }

  /* ── Glows ── */
  .hiw-glow {
    position:fixed; pointer-events:none; z-index:0;
    border-radius:50%; filter:blur(100px);
  }
  .hiw-glow-1 {
    width:600px; height:600px; top:-100px; right:-120px;
    background:radial-gradient(circle, rgba(200,160,101,.09) 0%, transparent 65%);
  }
  .hiw-glow-2 {
    width:480px; height:480px; bottom:-40px; left:-80px;
    background:radial-gradient(circle, rgba(31,61,58,.28) 0%, transparent 65%);
  }

  /* ── Orbs ── */
  @keyframes flt1 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-22px)} }
  @keyframes flt2 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(16px)} }
  .hiw-orb { position:fixed; border-radius:50%; pointer-events:none; z-index:0; }
  .hiw-orb-1 {
    width:220px; height:220px; top:18%; right:5%;
    background:rgba(200,160,101,.05); filter:blur(44px);
    animation:flt1 11s ease-in-out infinite;
  }
  .hiw-orb-2 {
    width:160px; height:160px; bottom:22%; left:4%;
    background:rgba(31,61,58,.2); filter:blur(32px);
    animation:flt2 14s ease-in-out infinite;
  }

  /* ── Container ── */
  .hiw-container {
    position:relative; z-index:1;
    max-width:1080px; margin:0 auto; padding:0 24px 100px;
  }

  /* ── Language bar ── */
  .hiw-lang-row { display:flex; justify-content:flex-end; padding:28px 0 0; }
  .hiw-lang-row.rtl { justify-content:flex-start; }
  .hiw-lang-bar {
    display:inline-flex; gap:4px;
    background:rgba(255,255,255,.04);
    border:1px solid rgba(200,160,101,.18);
    border-radius:30px; padding:4px;
  }
  .hiw-lang-btn {
    padding:6px 16px; border-radius:24px; border:none; cursor:pointer;
    font-family:'Montserrat',sans-serif; font-size:11px; font-weight:700;
    letter-spacing:.12em; transition:all .22s;
    background:transparent; color:rgba(255,255,255,.38);
  }
  .hiw-lang-btn.active { background:#c8a065; color:#060d0a; }
  .hiw-lang-btn:hover:not(.active) { color:rgba(255,255,255,.8); }

  /* ══ HERO ══ */
  .hiw-hero { text-align:center; padding:56px 0 76px; }

  .hiw-logo-badge {
    display:inline-flex; align-items:center; justify-content:center;
    padding:8px 24px; border-radius:40px; margin-bottom:28px;
    background:rgba(200,160,101,.07); border:1px solid rgba(200,160,101,.2);
    min-height:52px; min-width:130px;
  }
  .hiw-logo-img {
    height:34px; width:auto; object-fit:contain;
    filter:brightness(1.1) drop-shadow(0 0 6px rgba(200,160,101,.3));
  }
  .hiw-logo-fallback {
    display:none;
    font-family:'Cormorant Garamond',serif; font-size:17px;
    font-weight:500; color:#c8a065; letter-spacing:.1em;
  }

  .hiw-hero-title {
    font-family:'Cormorant Garamond',serif;
    font-size:clamp(44px,7vw,84px); font-weight:300;
    color:#fdf8f2; line-height:1.08; letter-spacing:-.01em; margin-bottom:18px;
  }
  .hiw-hero-sub {
    font-family:'Montserrat',sans-serif; font-size:14px; font-weight:300;
    color:rgba(253,248,242,.42); max-width:560px;
    margin:0 auto 36px; line-height:1.85;
  }

  .hiw-divider { display:flex; align-items:center; justify-content:center; gap:14px; }
  .hiw-divider-dot { width:5px; height:5px; border-radius:50%; background:#c8a065; opacity:.55; }
  .hiw-divider-bar {
    width:90px; height:1px;
    background:linear-gradient(90deg, transparent, rgba(200,160,101,.7), transparent);
  }

  /* ══ TIMELINE ══ */
  .hiw-timeline-wrap {
    position:relative; margin-bottom:90px; padding-bottom:24px;
  }

  /* Spine — zero-width wrapper ensures it sits exactly at 50% */
  .hiw-spine {
    position:absolute; left:50%; top:0; bottom:0;
    width:0; transform:translateX(-50%);
    pointer-events:none; z-index:0;
  }
  .hiw-spine-line {
    position:absolute;
    left:50%; top:28px; bottom:28px;
    transform:translateX(-50%);
    width:1px;
    background:linear-gradient(
      to bottom,
      transparent 0%,
      rgba(200,160,101,.38) 5%,
      rgba(200,160,101,.38) 95%,
      transparent 100%
    );
  }

  /* Step row — strict 3-column grid so bubble is ALWAYS in column 2 */
  .hiw-step-row {
    display:grid;
    grid-template-columns:1fr 72px 1fr;
    grid-template-rows:auto;
    align-items:center;
    min-height:118px;
    margin-bottom:28px;
    position:relative; z-index:1;
  }

  /* Slot layout */
  .hiw-slot { display:flex; align-items:center; }
  .hiw-slot-left   { grid-column:1; justify-content:flex-end;  padding-right:26px; }
  .hiw-slot-center { grid-column:2; justify-content:center; }
  .hiw-slot-right  { grid-column:3; justify-content:flex-start; padding-left:26px; }

  /* ── Card ── */
  .hiw-card {
    width:100%; max-width:420px;
    padding:22px 24px 20px; border-radius:18px;
    background:rgba(255,255,255,.028);
    border:1px solid rgba(200,160,101,.14);
    backdrop-filter:blur(12px);
    box-shadow:0 8px 32px rgba(0,0,0,.3), inset 0 1px 0 rgba(255,255,255,.04);
    transition:border-color .3s, transform .3s, box-shadow .3s;
  }
  .hiw-card:hover {
    border-color:rgba(200,160,101,.38);
    transform:translateY(-4px);
    box-shadow:0 22px 52px rgba(0,0,0,.42);
  }

  .hiw-card-top {
    display:flex; align-items:center; gap:10px; margin-bottom:11px;
  }
  .hiw-card-glyph {
    font-size:16px; color:#c8a065; line-height:1; flex-shrink:0;
  }
  .hiw-card-label {
    font-family:'Montserrat',sans-serif; font-size:10px; font-weight:700;
    letter-spacing:.2em; color:rgba(200,160,101,.55);
  }
  .hiw-card-title {
    font-family:'Cormorant Garamond',serif; font-size:22px; font-weight:500;
    color:#fdf8f2; line-height:1.2; margin-bottom:8px;
  }
  .hiw-card-desc {
    font-family:'Montserrat',sans-serif; font-size:12px; font-weight:300;
    color:rgba(253,248,242,.48); line-height:1.8;
  }

  /* ── Number bubble ── */
  .hiw-bubble {
    position:relative;
    width:56px; height:56px; border-radius:50%;
    background:linear-gradient(140deg, #17362b 0%, #c8a065 100%);
    border:2px solid rgba(255,255,255,.14);
    display:flex; align-items:center; justify-content:center;
    box-shadow:0 0 0 10px rgba(200,160,101,.06), 0 10px 28px rgba(0,0,0,.45);
    z-index:2; flex-shrink:0;
  }
  .hiw-bubble-num {
    font-family:'Montserrat',sans-serif;
    font-size:20px; font-weight:700; color:#fff; line-height:1;
  }
  .hiw-bubble-ring {
    position:absolute; inset:-8px; border-radius:50%;
    border:1px solid rgba(200,160,101,.2);
    animation:ringPulse 3s ease-in-out infinite;
  }
  .hiw-bubble-ring-2 {
    inset:-17px; border-color:rgba(200,160,101,.1);
    animation-delay:1.5s;
  }
  @keyframes ringPulse {
    0%,100% { opacity:.7; transform:scale(1); }
    50%      { opacity:.1; transform:scale(1.12); }
  }

  /* ── Mobile ── */
  @media(max-width:680px) {
    .hiw-spine { display:none; }
    .hiw-step-row {
      grid-template-columns:52px 1fr;
      min-height:unset; margin-bottom:18px;
      align-items:flex-start;
    }
    /* bubble → col 1, row 1 */
    .hiw-slot-center { grid-column:1; grid-row:1; align-items:flex-start; padding-top:3px; }
    /* both cards → col 2, row 1 */
    .hiw-slot-left,
    .hiw-slot-right {
      grid-column:2; grid-row:1;
      justify-content:flex-start; padding:0;
    }
    .hiw-bubble { width:44px; height:44px; }
    .hiw-bubble-num { font-size:16px; }
    .hiw-card { max-width:100%; }
  }

  /* ══ FEATURES ══ */
  .hiw-features-wrap { margin-bottom:80px; }

  .hiw-section-eyebrow {
    text-align:center; margin-bottom:36px;
    font-family:'Montserrat',sans-serif; font-size:11px; font-weight:700;
    letter-spacing:.22em; color:#c8a065;
  }

  .hiw-feat-grid {
    display:grid; grid-template-columns:repeat(4,1fr); gap:16px;
  }
  @media(max-width:860px){ .hiw-feat-grid{ grid-template-columns:repeat(2,1fr); } }
  @media(max-width:480px){ .hiw-feat-grid{ grid-template-columns:1fr; } }

  .hiw-feat-card {
    position:relative; padding:26px 22px; border-radius:18px; overflow:hidden;
    background:rgba(255,255,255,.028); border:1px solid rgba(200,160,101,.12);
    backdrop-filter:blur(10px);
    box-shadow:0 8px 28px rgba(0,0,0,.26), inset 0 1px 0 rgba(255,255,255,.04);
    transition:border-color .3s, transform .3s, box-shadow .3s;
  }
  .hiw-feat-card::after {
    content:''; position:absolute; inset:0;
    background:linear-gradient(135deg, rgba(200,160,101,.07) 0%, transparent 55%);
    opacity:0; transition:opacity .35s; pointer-events:none;
  }
  .hiw-feat-card:hover {
    border-color:rgba(200,160,101,.32); transform:translateY(-5px);
    box-shadow:0 22px 52px rgba(0,0,0,.38);
  }
  .hiw-feat-card:hover::after { opacity:1; }

  .hiw-feat-icon {
    width:46px; height:46px; border-radius:12px;
    background:rgba(200,160,101,.1); border:1px solid rgba(200,160,101,.2);
    display:flex; align-items:center; justify-content:center;
    margin-bottom:16px; position:relative; z-index:1;
  }
  .hiw-feat-title {
    font-family:'Cormorant Garamond',serif; font-size:19px; font-weight:500;
    color:#fdf8f2; margin-bottom:10px; line-height:1.25;
    position:relative; z-index:1;
  }
  .hiw-feat-desc {
    font-family:'Montserrat',sans-serif; font-size:12px; font-weight:300;
    color:rgba(253,248,242,.46); line-height:1.75;
    position:relative; z-index:1;
  }

  /* ══ CTA ══ */
  @keyframes shimSlide {
    from { transform:translateX(-100%) skewX(-12deg); }
    to   { transform:translateX(200%)  skewX(-12deg); }
  }

  .hiw-cta {
    position:relative; border-radius:24px; overflow:hidden;
    background:linear-gradient(135deg, #0d1f18 0%, #1a3828 45%, #0f2318 70%, #1c1206 100%);
    border:1px solid rgba(200,160,101,.25); padding:64px 40px;
    box-shadow:0 32px 80px rgba(0,0,0,.55), inset 0 1px 0 rgba(200,160,101,.1);
  }
  .hiw-cta-shimmer {
    position:absolute; inset:0; pointer-events:none;
    background:linear-gradient(105deg, transparent 35%, rgba(200,160,101,.08) 50%, transparent 65%);
    animation:shimSlide 4.5s ease-in-out infinite;
  }
  .hiw-cta-glow {
    position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);
    width:550px; height:320px; border-radius:50%; pointer-events:none;
    background:radial-gradient(ellipse, rgba(200,160,101,.11) 0%, transparent 65%);
  }
  .hiw-cta-inner { position:relative; z-index:1; text-align:center; }
  .hiw-cta-eyebrow {
    font-family:'Montserrat',sans-serif; font-size:10px; font-weight:700;
    letter-spacing:.26em; color:rgba(200,160,101,.7); margin-bottom:14px;
  }
  .hiw-cta-title {
    font-family:'Cormorant Garamond',serif;
    font-size:clamp(28px,5vw,52px); font-weight:400;
    color:#fdf8f2; margin-bottom:34px; line-height:1.18;
  }
  .hiw-cta-btn {
    display:inline-flex; align-items:center; gap:10px;
    padding:14px 38px; border-radius:40px;
    background:#c8a065; color:#060d0a;
    font-family:'Montserrat',sans-serif; font-size:12px; font-weight:700;
    letter-spacing:.14em; text-decoration:none;
    transition:background .25s, transform .2s, box-shadow .25s;
    box-shadow:0 8px 28px rgba(200,160,101,.38);
  }
  .hiw-cta-btn:hover {
    background:#d4ae78; transform:translateY(-2px);
    box-shadow:0 14px 38px rgba(200,160,101,.48);
  }
`;